// Buying credits: the top-up history, opening one, collecting on it, settling it, and what was
// given back. `tenancy.billing.manage` on all six.
//
// **The attempt answers everything the browser confirms with** — the client secret, the
// publishable key of the invoicing service's provider account and, for a direct charge, the
// connected account (its L18, `docs/plans/billing-ledger.md` B7f). The payment modal hands the
// three to the provider's script and never keeps them; what it waits on afterwards is the top-up
// reading `completed`, which only the invoicing service's `sale.paid` writes.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type CreditPurchaseView = components['schemas']['CreditPurchaseView']
export type CreditPurchasePage = components['schemas']['Page_CreditPurchaseView']
export type OpenCreditPurchaseRequest = components['schemas']['OpenCreditPurchaseRequest']
export type CreditPaymentOpened = components['schemas']['CreditPaymentOpened']
export type OpenCreditPaymentRequest = components['schemas']['OpenCreditPaymentRequest']
export type CreditPurchaseReconciled = components['schemas']['CreditPurchaseReconciled']
export type CreditRefundEntry = components['schemas']['CreditRefundEntry']

/** The walk: `limit` and `after`, newest first. */
export type CreditPurchasesQuery = NonNullable<
  operations['orgs_credit_purchases_list']['parameters']['query']
>

/**
 * A page of top-ups, newest first — every one ever opened, the ones that expired unpaid included:
 * a row records a gesture, and a gesture that came to nothing is part of the history.
 */
export function listCreditPurchases(
  query: CreditPurchasesQuery = {},
  signal?: AbortSignal,
): Promise<CreditPurchasePage> {
  return api.get<CreditPurchasePage>(
    withQuery('/orgs/current/credits/purchases', query),
    undefined,
    signal,
  )
}

/** One top-up. Another tenant's uid is `not_found`, never `forbidden`. */
export function readCreditPurchase(
  purchaseId: string,
  signal?: AbortSignal,
): Promise<CreditPurchaseView> {
  return api.get<CreditPurchaseView>(
    `/orgs/current/credits/purchases/${encodeURIComponent(purchaseId)}`,
    undefined,
    signal,
  )
}

/**
 * Every refund asked for on a top-up, oldest first. Read-only on this surface: asking for one is
 * an operator's act, through `videas billing refund`.
 */
export function listCreditPurchaseRefunds(
  purchaseId: string,
  signal?: AbortSignal,
): Promise<CreditRefundEntry[]> {
  return api.get<CreditRefundEntry[]>(
    `/orgs/current/credits/purchases/${encodeURIComponent(purchaseId)}/refunds`,
    undefined,
    signal,
  )
}

/**
 * Open a top-up. `idempotencyKey` is **one pay gesture, one key**, unique over every top-up this
 * organization ever opened, and a retry after a network timeout sends the key the first attempt
 * sent — that is what makes a double submit one row and one sale. Nothing is charged and the
 * wallet does not move: credits are delivered when the sale is paid.
 */
export function openCreditPurchase(
  body: OpenCreditPurchaseRequest,
  idempotencyKey: string,
): Promise<CreditPurchaseView> {
  return api.post<CreditPurchaseView>('/orgs/current/credits/purchases', body, {
    'Idempotency-Key': idempotencyKey,
  })
}

/**
 * Open the attempt the payment page collects on. The key is **the attempt's and not the top-up's**:
 * a declined attempt is terminal, so a retry after a refusal carries a new one, and creating the
 * new attempt cancels the superseded one at the provider.
 *
 * `save_instrument` keeps the card for the automatic top-up, and is refused `409` unless that is
 * switched on — the consent it records is what the card is kept under.
 *
 * The `client_secret` it answers is stored nowhere — a page that lost it opens another attempt.
 */
export function openCreditPayment(
  purchaseId: string,
  idempotencyKey: string,
  body: OpenCreditPaymentRequest = {},
): Promise<CreditPaymentOpened> {
  return api.post<CreditPaymentOpened>(
    `/orgs/current/credits/purchases/${encodeURIComponent(purchaseId)}/payments`,
    body,
    { 'Idempotency-Key': idempotencyKey },
  )
}

/**
 * Ask the invoicing service what became of a top-up, and settle it if it is paid. The recovery for
 * a payment whose event has not arrived; it credits the wallet exactly once whatever else runs.
 */
export function reconcileCreditPurchase(purchaseId: string): Promise<CreditPurchaseReconciled> {
  return api.post<CreditPurchaseReconciled>(
    `/orgs/current/credits/purchases/${encodeURIComponent(purchaseId)}/reconciliations`,
  )
}

/**
 * Cancel an open top-up nobody paid for (`billing-ui.md` U8). The sale is cancelled at the
 * invoicing service first, so nothing can still be paid once this answers. Idempotent; `409` when
 * the top-up is paid — here, or a moment ago at the invoicing service, in which case its credits
 * were delivered — or held in a state a cancellation cannot undo. Nothing is ever charged.
 */
export function cancelCreditPurchase(purchaseId: string): Promise<CreditPurchaseView> {
  return api.post<CreditPurchaseView>(
    `/orgs/current/credits/purchases/${encodeURIComponent(purchaseId)}/cancellations`,
  )
}
